/**
 * PostcodeSearch Component
 * Search input for UK postcodes with optional building query
 */

import React from 'react';
import { Search } from 'lucide-react';
import { DEFAULTS, UI_STATES } from '../utils/addressConstants';

/**
 * PostcodeSearch Component
 * @param {Object} props - Component props
 * @param {string} props.postcode - Current postcode value
 * @param {string} props.query - Current query value
 * @param {Function} props.onPostcodeChange - Postcode change handler
 * @param {Function} props.onQueryChange - Query change handler
 * @param {Function} props.onSearch - Search trigger handler
 * @param {boolean} props.isLoading - Loading state
 * @param {string} props.validationMessage - Validation error message
 * @param {string} props.uiState - Current UI state
 * @param {boolean} props.showQueryInput - Whether to show building query input
 * @param {string} props.className - Additional CSS classes
 */
const PostcodeSearch = ({
  postcode,
  query,
  onPostcodeChange,
  onQueryChange,
  onSearch,
  isLoading = false,
  validationMessage = '',
  uiState = UI_STATES.IDLE,
  showQueryInput = false,
  className = ''
}) => {
  /**
   * Handle postcode input change
   */
  const handlePostcodeChange = (e) => {
    const value = e.target.value.toUpperCase();
    onPostcodeChange(value);
  };

  /**
   * Handle query input change
   */
  const handleQueryChange = (e) => {
    const value = e.target.value;
    onQueryChange(value);
  };

  /**
   * Handle search button click
   */
  const handleSearchClick = () => {
    if (onSearch && postcode.trim()) {
      onSearch(postcode, query);
    }
  };

  /**
   * Handle Enter key press
   */
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchClick();
    }
  };

  const isSearching = uiState === UI_STATES.SEARCHING;
  const hasValidationError = !!validationMessage;

  return (
    <div className={`w-full ${className}`}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4">
          {/* Postcode Input */}
          <div className="flex flex-col">
            <label 
              htmlFor="postcode" 
              className="font-medium mb-1 text-gray-700"
            >
              Postcode
            </label>
            <div className="relative">
              <input
                id="postcode"
                type="text"
                value={postcode}
                onChange={handlePostcodeChange}
                onKeyPress={handleKeyPress}
                placeholder={DEFAULTS.PLACEHOLDER_POSTCODE}
                className={`w-full px-3 py-3 border rounded-md text-base transition-colors focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed ${
                  hasValidationError 
                    ? 'border-red-600 focus:border-red-600' 
                    : 'border-gray-300 focus:border-blue-600'
                }`}
                disabled={isSearching}
                maxLength={8}
                autoComplete="postal-code"
                aria-invalid={hasValidationError}
                aria-describedby={hasValidationError ? 'postcode-error' : undefined}
              />
            </div>
            {hasValidationError && (
              <div 
                id="postcode-error" 
                className="mt-1 text-sm text-red-600"
                role="alert"
                aria-live="polite"
              >
                {validationMessage}
              </div>
            )}
          </div>

          {/* Building Query Input (optional) */}
          {showQueryInput && (
            <div className="flex flex-col">
              <label 
                htmlFor="building-query" 
                className="font-medium mb-1 text-gray-700"
              >
                Building Name/Number <span className="text-gray-500 font-normal text-sm">(optional)</span>
              </label>
              <input
                id="building-query"
                type="text"
                value={query}
                onChange={handleQueryChange}
                onKeyPress={handleKeyPress}
                placeholder={DEFAULTS.PLACEHOLDER_QUERY}
                className="w-full px-3 py-3 border border-gray-300 rounded-md text-base transition-colors focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
                disabled={isSearching}
                maxLength={50}
                autoComplete="address-line1"
              />
              <div className="mt-1 text-xs text-gray-500">
                Enter building name or number to narrow down results
              </div>
            </div>
          )}
        </div>

        {/* Search Button */}
        <div className="flex justify-start">
          <button
            type="button"
            onClick={handleSearchClick}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-pest-teal to-teal-600 text-white border-none rounded-lg font-semibold cursor-pointer transition-all duration-200 hover:from-teal-600 hover:to-teal-700 hover:shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed disabled:shadow-none"
            disabled={isSearching || !postcode.trim()}
            aria-label="Search for addresses"
          >
            {isSearching ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden="true"></div>
                Searching...
              </>
            ) : (
              <>
                <Search size={18} aria-hidden="true" />
                Find Address
              </>
            )}
          </button>
        </div>
      </div>

    </div>
  );
};

export default PostcodeSearch;